import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface InventoryPaginationProps {
    currentPage: number;
    totalPages: number;
    totalProducts: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

export const InventoryPagination: React.FC<InventoryPaginationProps> = ({
    currentPage,
    totalPages,
    totalProducts,
    pageSize,
    onPageChange,
    onPageSizeChange,
}) => {
    if (totalPages <= 1) return null;

    const numberedPages = () => {
        const pages: React.ReactNode[] = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        const endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`min-w-[36px] px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        i === currentPage
                            ? "text-white bg-blue-600 border border-blue-600 shadow-sm"
                            : "text-slate-600 bg-white border border-slate-300 hover:bg-slate-50"
                    }`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    return (
        <div className="mt-5 pt-4 border-t border-slate-200">
            {/* Mobile: compact prev / page / next + page-size */}
            <div className="sm:hidden">
                <div className="flex items-center justify-between gap-2">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" /> Prev
                    </button>
                    <span className="text-sm text-slate-600">
                        Page <span className="font-semibold text-slate-900">{currentPage}</span> of {totalPages}
                    </span>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-500">
                    <span>{totalProducts.toLocaleString()} products</span>
                    <span>·</span>
                    <label className="flex items-center gap-1.5">
                        Show
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border border-slate-300 rounded-md px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        per page
                    </label>
                </div>
            </div>

            {/* Desktop: full controls */}
            <div className="hidden sm:flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                    <label className="text-sm font-medium text-slate-600">Per page</label>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" /> Previous
                    </button>

                    <div className="flex items-center gap-1">
                        {currentPage > 3 && (
                            <>
                                <button
                                    onClick={() => onPageChange(1)}
                                    className="min-w-[36px] px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                    1
                                </button>
                                {currentPage > 4 && <span className="px-2 text-slate-400">…</span>}
                            </>
                        )}

                        {numberedPages()}

                        {currentPage < totalPages - 2 && (
                            <>
                                {currentPage < totalPages - 3 && <span className="px-2 text-slate-400">…</span>}
                                <button
                                    onClick={() => onPageChange(totalPages)}
                                    className="min-w-[36px] px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                                >
                                    {totalPages}
                                </button>
                            </>
                        )}
                    </div>

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <ChevronRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="text-sm text-slate-500">
                    <span className="font-medium text-slate-700">{totalProducts.toLocaleString()}</span> products · page{" "}
                    <span className="font-medium text-slate-700">{currentPage}</span> of{" "}
                    <span className="font-medium text-slate-700">{totalPages}</span>
                </div>
            </div>
        </div>
    );
};
